import { computed, ref } from 'vue';
import { useMutation, useQuery } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { useI18n } from 'vue-i18n';
import { loadMessages } from '~/plugins/i18n';
import { dayjs } from '~/plugins/dayjs';
import useAuth from './useAuth';

/**
 * Unified composable to:
 * - manage the application's current language (i18n)
 * - provide language options for selectors
 * - validate language fields in forms
 * - handle formatting utility functions (e.g., AUTO → null)
 * - manage the language configuration in BE
 */
export default function useLanguage() {
  // This is the current locale used by the i18n plugin
  const { t, locale } = useI18n();
  const { activeUser } = useAuth();

  // Call to retrieve the available languages and the default one
  const { result: tenantResult, onResult } = useQuery(
    gql`
      query TenantLanguages {
        tenant {
          availableLanguages
          defaultLanguage
        }
      }
    `,
    {},
    () => ({
      enabled: !!activeUser.value, // Prevent calls when not authenticated
      fetchPolicy: 'cache-first', // Prevent multiple calls
    }),
  );
  // Tells when we are done loading the available lanaguages from backend
  const loadingFromBackendDone = ref(false);
  onResult((queryResult) => {
    loadingFromBackendDone.value = !queryResult?.loading;
  });

  // Update the language configuration
  const {
    mutate: updateLanguageConfiguration,
    loading: updatingLanguageConfiguration,
  } = useMutation(
    gql`
      mutation UpdateLanguageConfiguration(
        $languages: [UserLanguage!]!
        $default: UserLanguage!
        $force: UserLanguage
      ) {
        updateLanguageConfiguration(
          input: { languages: $languages, default: $default, force: $force }
        )
      }
    `,
  );

  // List of all the allowed languages for UI (used in header dropdowns, etc.)
  const allowedLanguages = computed(() => {
    const allLanguagesHandled = [
      {
        name: t('common.language.french'),
        value: 'fr',
        props: {
          avatar:
                  'iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAGhSURBVHgBtZW7TgJBFIb/GQotMfZmbbw0RgpraU00LvoC+ARCJd0ulYmNvAH4AogFsRSfAGMjVGxMjIXFLp2F7HhOVmAVdgOy8yUz2cxm5j+XOWeAOMzbdL/ftzzPe6DRo6F+hstrrusW3kuXRtwRYvrB9wZSg+ra6lL2+Wovbj8+dnLwX99qPmR5Gx3n7385seO0eQ45aEMhixlREHkJ1e5gsxAvcNK0oFSFvtKYExXsue5iw5ouwJZD2VgQ8sYOexIIcMzV4ocPocRaL9gyxgLSZ7fmDksUHC4BVQ0E2HqoPJIn24ORlnRjTGjiE8sFSQE7hiYoTPt0fbELfRic5MSSGyWgFRZwoAmqB4+T7EATVNVPlGT1CG2oOwn/qwJNkAcNiUbOozC1kDBUAzV+H4JbNEid0ewhITi5/ADxdyDQOHBoLiMhqNmNXrdxHdQPKRdqYRGKe3kL3VFefxda/cimuYh/hEsEe4pkuR1en6xk9sRPZciUG8x+fMuHyIQtDwlHU6q2jQvTMIUQ3HG5KQ77lsNDUQ15K5nKOpxIj78BrRmU+yXZey0AAAAASUVORK5CYII=',
        },
        enabled: true,
      },
      {
        name: t('common.language.english'),
        value: 'en',
        props: {
          avatar:
                  'iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAKwSURBVHgBtVVfSJNRFD93fLPyH1+FgmByB5aVL4rQWzUpX1rhUOpV99jTFvnUQ/v2Hrggeqq2eomCaoJLaMFmGMSgXMFyWLlPKiIl/Vi6apvezr3uj605N9x+cDbOuff+fuc75/4hUAzmJzLoaqxNjXrjYixBMULZIxMf0dBCaGNoHkKIuhWFrlBQuT9Dm4af+eWG2mXavEe5MthupM21dNMUGc2INooWZYy50GghLpIfwInWuYW40jMyJWurSXBaOsF6lkLv1VcQCP8A7VyiEA+wRFJb9fgcrRO3nFsLDHjt5mPNCmYLzvGoCBk794EWTwEXUxd+QeTxZSgGAkzpgFlHxs+VaNBrxTyUkBoDt/9LNhwIL4FcqxfkpYABUSLQYcsJcpgnKOjWprsMjbL9wiGQ6/RgufFWZG01GWC4txUcD2eF8PzSg+1VJElr893p5s2XuN9zsMH++pMmh6IxQYqNwIzjYu78Yhz4V3mC34Xf5r8HJYBvAhdaL0lnH82OYPYcQmiTn4mlt2mp2Ct9uHncfP7aG5ElJzN27gdeSV77jIDVROG6VxUCSfVryewMmE1qb6nrH8Iah1zvkUyCUctRMdQ9MiUIFewJ36Z3AxuNnzOcgjJwUupzBLtSa+vC4zvlkissss6UyBP8Bu7A56xfJiiBgXFWzortzkE+CJ7csgTKBT9oKlQPWrUFQtJHQ9+kDtaNUA1I+jESBSr/gV3LUAXgvWTQGUDlj0cAKgy8Vd1HIKKKy24GDtMDvtvTRJJkqABYKqX9fPmuu0W5qOaCjNlY5WArrMyYwnYO5d9S5WHl6Qvbiue5nSUSZZWL1NRo9ebTjvozJ4o8mWnwnuCfgo0agtLoA3gdWHhT/xsptowLESBmLF4/inWxjYeEL1PxR0V/cjf8dqZ3YkH8BVtqpciMZOc1AAAAAElFTkSuQmCC',
        },
        enabled: true,
      },
      {
        name: t('common.language.german'),
        value: 'de',
        props: {
          avatar:
                  'iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAMAAADXqc3KAAAAAXNSR0IB2cksfwAAAAlwSFlzAAALEwAACxMBAJqcGAAAAOdQTFRFAAAAMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzMzPDAyPy8yQi8yRC4yRi0ySCwxSiwxTSsxTyoxTyoxTisxTCsxRi0yQS8yPy8y2AAn2AAn2AAn2AAn2AAn2AAn2AAn2AAn2AAn85k79J889aM99aM99Z88/9pE/9pE/9pE/9pE/9pE/9pE/9pE/9pE/9pE/9pE/9pE/9pE/9pE/9pE/9pE/9pE/9pE/9pE/9pE/9pE/9pE/9pE8UjRZQAAAE10Uk5TABJmtNjyZSGa+f+ZHE/1Unn+d1P9VS72MKgf5Ofr7/L2+v3//fnu5+N4/3C8uODY+PAe7f7/7qj/IPUfVf1Td3JS+U8cmZoSZbTY8mbPmx8mAAAA0ElEQVR4nHWSyQrCMBRF300r0YIoCIL+gSBOGxEEEQS/WVAQwYVfoiAIVbHU1sYO6UCb3EUW97y8IS+gRIhEQhiekEZ88shNJBjeGWjCp1wmbAnacKkojkcMOg6VZOEegi5eZRDmvoF6dsUnal3RRyrZtie4Y+GJARQXooBhoAQGRr4S1DD5KgHH1NWAsadJpSnOdO0KzPIXL9jMB81VV8QFtEC1CsM5DF9WRqzhFC9qVR6lfpSrXeNTzNM4UPoZNvilyDKDPWWAaJt9n11i/AGFPTDS7XEo6wAAAABJRU5ErkJggg==',
        },
        enabled: true,
      },
      {
        name: t('common.language.spanish'),
        value: 'es',
        props: {
          avatar:
                  'iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAMAAADXqc3KAAAAAXNSR0IB2cksfwAAAAlwSFlzAAAOxAAADsQBlSsOGwAAAIdQTFRFAAAA2AAn2AAn2AAn2AAn2AAn2AAn2AAn2AAn2AAn2AAn2AAn2AAn2AAn2AAn2AAn2AAn2AAn2AAn2AAn2AAn2AAn2AAn51My4TEu4S4t6mI0/9pE/9pE/9pE/9pE/9pE/9pE/9pE/9pE/9pE/9pE6WE02AAn2AAn2AAn2AAn2AAn2AAn2AAnMLsYOAAAAC10Uk5TAAI4iMXo/PvDhAEsqu7/pChg85KFVek47v8fiP93xej8+8ODcB8n33XgH3dxZD3dhwAAAL9JREFUeJyFkl0LgjAUhs/rR5piEBERQmlF///XRPUHukiIIEeabTlnZbrqvdjF82zjnJ2BqkCFm8hcpohcPAnVDhIGcvYUQxT0jrBOtRhdHGomCxIpPId98tIMUgbynYLacdMU8I0OJ+Ick1c9zZS1hTouG5txrTAwv2uFiahbk4yF+KYV9verFrlW9LDMtML91mAAzxYagTNofegemYr9j2cn6vPWoOJtPdoxrg0eJkf69xlKFWFXfZ8V3yjyAKPpNWLu7/CmAAAAAElFTkSuQmCC',
        },
        enabled: true,
      },
      {
        name: t('common.language.italian'),
        value: 'it',
        props: {
          avatar:
                  'iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAACXBIWXMAAACxAAAAsQHGLUmNAAAAGXRFWHRTb2Z0d2FyZQB3d3cuaW5rc2NhcGUub3Jnm+48GgAAAlhJREFUSIljZMAD9lxeo2cq52r///9/bwYGBgUGBgYZqNQTBgaGB4yMjFs+zV13ULYo8TIuMxixCS45OsH01ef7y37++aacadONVQ0MvLGLY2AS5n/EZWUQLtFafAJdngldYO6hlt4LT/eevP/2qsrff3/xGs7AwMDA8OcPw6/9p+Q+Tlp67GFkYRdeC+YcaFh/8fHBop+/vxM2GA38//yV8fuK7aUPQvPWYbVgweGOzktPjwb8Z/hPqtlItvxn+LFmV+CjiIJuFAtWHJ1gevnZkVKKDEe2ZMvB4gfZDUZwC559ur/i158fJAcLLvDvyzfGv5dvr2RgYGBgWnN8ivnD9zeUqGU4DPw5dl7lUWGLCdPb7y+r/v3/S23zGf7/+cvw78HzKqaPP95ZUt10KPj7/JU104fvrwRpZsGj54JM3399ZaGVBf/ff2JlYqRa2sEEjIyM/5k4WLn/0MwGAd4/TAKcYu9pZT6znOR7Jj4OoWO0soBJQvQIkwi3dAszE/XjmZGFmYFZUaqdKcQ844y8oPpdalvAamV0S66/5gwTAwMDgwSPfAQbCwcVSjoIYOLh+s9jrhfOwAAt7CJtis5oS1i2MWKv4EgDjIwMbN52PWLdZRfgFjAwMDAk2VfX6ErZrKfIEiYmBs5wzzUKKyeWwYWQ5VMd64MMZGx72Fk4SQ4uRj6e/9wRnp3yK/pDUexEV5hkX1eqL2VtKC+kdZOJkYmwRSwsDGz2pvd44v2NZZf1VWBYjE/vzosrdCwUPGHNFkUG1GbLPUZGxq0fZ60+JFeacgWXGQCMnc/pa8nh0wAAAABJRU5ErkJggg==',
        },
        enabled: true,
      },
      {
        name: t('common.language.polish'),
        value: 'pl',
        props: {
          avatar:
                  'iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAA10lEQVR42rVW2w3DIAz0JqgLdCVGYABGYgQG4qOffPLh2u0ppVWUR2OfdFIE5A4wxhBtgJmDMAursPEHDW3aF+gsIFyEg/cxMDYcFY/Czueh/8Q98cTXkbZmboW4tufd0KB/xQRBskaZZz8cDMZrFTjLXsiEhPFCpZ8MtUYjdgY9bnf2pBo0R4OmBtXRoKpBdjTIahCEw0FcNd/XhXwUB4Oy3EVYRTcU78vsJ5NoaLBeeKQjGYinzaqGlfQ/tyUeqsuISTl4ugbGnn9dwCgjGeeMb2jLe8JPH0q/BvTNF/wAAAAASUVORK5CYII=',
        },
        enabled: true,
      },
      {
        name: t('common.language.chinese'),
        value: 'zh',
        props: {
          avatar:
                  'iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAYAAABXAvmHAAAFgklEQVR4nM2YT1BbVRTGvxeEBIQmtMi00A5pRwXGBemMY5fFGV0L7p2GrY4DruzogrDQcddo1S2h41Zt1zo1LuvoGFYEdeRVpTIVSsKfQqBN/M5LXvry/93HI81v5vSel7aP891z7rknaHCBFYQCB9h/g24wB4wDuSDooxQd0HQNiIN+B3y3ziORon8k+D5nSNAZ7F2hGwYQojkhQYt50bngVIyygCWMBDXkpumGAQRobpCixXLQPh1FUocCSgKWMTKbQ26GboB2HKQ0aNFhJOfo20KjNeQ3vBTK4tE83RCtGSSYjUk72WgogCUTZslcoxugNZMURbxHETHUoa6AQvDzdJ8aFDFVT0RNAa0QvEk9EVUFtFLwJho8k8NYukm3hAoBhQP7A90ArZWQM3GRmdBhoUJAEsO/cgnRWpHECJYvci1SIoClE2HpzNJ1Dc8JD7JbWXqAd7QDXZe82Ixt88kZzMIcsxBBgaIABh9k8LL7AZpriIBzX/Vj6+td9F7pwfrnaWx9s8u/cUxJKRUFsHSiXKZprtP9eicGv+ijB2x/v4fNhR3s3dnnkzMooJgFQ0BhMFuhG6A1RHbVLAs7nP7kJPxvPksP2E8eYue7PWx9u4vDfx7xE0ekOACelwHQEJDE6AyQvUa3Ie1nn0H3a52261h23zvSgUzygEJOYfXt9SPtvgmzMMUsxAoC7Hee3nAPOl/x4h4DUeXUu35sXE/TcwWjI2mF8tnkB7YYunUavtF2/P7yqlIZVUNKUbJpHmrJrkpZsYx6NXafMLvPPJ8bIj/gwu0z9IB/rz4o/uCj8PwvZ7HxWZotth0Pf8oovVNuZ43lE6U/TWuIlE//BwF6+W7ipIysyIacu9HPtQ2Hq4/x56v3+Kl9eA7mJANxZuAynxtilo/JUcpIDrcc6rYe4xgaqGaVAn6UDKzQD6KA7MrAl33wjTwJVIXH2zmlTiNZlcO9dnXD6FabC9sqm6KLgBydCvo/7OXN2U3PPlLDErxCAOi85EMXu5rT7lRTgCAvH2Q2rGmuxf2PU7bvhnKkG6mItlJXgCAvFxGyS9WQwye7nlk64FPzaShAOMEx4AzHgWq40Y1kSnW6AdKFdHahIfo1kUPdwwunFssv/s0/nXPh9gD0iTXlMmIXuisC4hRwmc9VkRJ64edBenlkGJPd8k/mhzNh9Z11Y0BTQc6XlKWnx4OT4W6k2T6lHGVmsvsuCjDaaJR+zYvMWj4yBt//aJNe/nPpVHLA05ws195/wE/VkBZqXoyC9f12oIA5yUCYGZjnc1WkfLq4W3JQy3u7eWe0D7ThD15qTrBejqoXozFK1BvmpHykA0nw9V4sF5FK6k1kA+S7gry/j+/YZ2mq3MTGMMdVxukElzFaCSKgXuBW7PxbEbrDriVnSCj/P+XPDVjkOB0qCLD/hUYVOaz+yS4GfcgzE8Bfb/1XUYpOYP0/+UJTKCMdgJ/mOnLYzbFkn0Ie3skgdWNbafYvI83yCRa/UgosoyiXaZrrWLuNXHwb17eYkXwZOYG7P8fdj4AUBbAbBdmNEnT9NNeQg/ocg5faFyEytKke9jLSFBCiAB2kKECgiAhFzNI9FmRkOLZfbJmwlCQLY7RWxOg8XItUCCj8cjcOl0vJBUpKx6RCgMBSCrOU5um2DHLr2vr1ukkrieDOT3HnY6hCTQFCK4ioF7xQV4CwjNGJHLIxNP9MpFk24WplY6WhAIGZCDIT8qIxWjNY5M5PcOd1NMCWABMKiVDIDF0/7ThIM/AoA4/AJkoCBIoIFkSE4Z4QI3CuMQavQwFlASb5ATAT5hRLc1xaixK4D76bMpjxWRnHAqyImANkxrPIhfg4DiDILA1xLcJA73LRAcQ90BId8MadBm3lf0R2TmLxM/hmAAAAAElFTkSuQmCC',
        },
        enabled: true,
      },
    ];

    let allowedFromBackend = window?.config?.locales || {};
    allowedFromBackend = Object.keys(allowedFromBackend);
    if (allowedFromBackend.length === 0) {
      return allLanguagesHandled;
    }

    // Return languages that are both in the backend config and the predefined list
    return allLanguagesHandled.filter(
      (language) => allowedFromBackend.includes(language.value),
    );
  });

  // List of all the languages that the tenant administrator has made available
  const availableLanguages = computed(() => {
    const graphqlLanguages = tenantResult.value?.tenant?.availableLanguages ?? [];
    if (graphqlLanguages.length === 0) {
      return allowedLanguages.value;
    }

    const normalized = graphqlLanguages.map((lang) => lang.toLowerCase());

    return allowedLanguages.value.filter((language) => normalized.includes(language.value));
  });

  // The default language coming from BE
  const defaultLanguage = computed(() => {
    const rawValue = tenantResult.value?.tenant?.defaultLanguage ?? 'en';
    return rawValue.toLowerCase();
  });

  // Form validation rules for language selector
  const languageRules = computed(() => [
    (v) => !!v || t('user.selectLanguage'),
  ]);

  /**
   * Returns the data URL for a language avatar (PNG or SVG base64).
   * PNG avatars start with "iVBOR"; SVG avatars start with "PHN2" (base64 of "<svg").
   */
  const getLanguageAvatarSrc = (avatar) => {
    if (!avatar) return '';
    const isPng = avatar.startsWith('iVBOR');
    const mime = isPng ? 'image/png' : 'image/svg+xml';
    return `data:${mime};base64,${avatar}`;
  };

  // Convert 'AUTO' to null for form compatibility
  const getUserLanguageFormInput = (language) => {
    const formatted = language?.toUpperCase?.();
    return formatted === 'AUTO' ? null : formatted;
  };

  // Set application language
  async function setLanguage(language) {
    let targetLanguage = language || navigator.language.split('-')[0];
    const isAllowed = allowedLanguages.value.some(
      (lang) => lang.value === targetLanguage && lang.enabled,
    );
    if (!isAllowed) {
      targetLanguage = 'en';
    }

    await loadMessages(targetLanguage);
    const dayjsLocale = targetLanguage === 'zh' ? 'zh-cn' : targetLanguage;
    dayjs.locale(dayjsLocale);
  }

  /**
   * Check if the translations have the current locale translation
   * @returns {boolean}
   * @param translations
   */
  function hasCurrentLocaleTranslation(translations) {
    return !!translations
            && !!translations[locale.value];
  }

  /**
   * Sanitize the translations object
   * @param translations
   * @returns {{}|{[p: string]: unknown}}
   */
  function sanitize(translations = null) {
    const result = {};
    let clone = JSON.parse(JSON.stringify(translations));
    if (!clone) {
      clone = {};
    }

    for (let i = 0; i < allowedLanguages.value.length; i += 1) {
      const lang = allowedLanguages.value[i].value;
      const value = clone[lang];
      if (!!value && typeof value === 'string') {
        result[lang] = value.trim();
      } else {
        result[lang] = null;
      }
    }

    return result;
  }

  return {
    hasCurrentLocaleTranslation,
    locale, // current language
    setLanguage, // change app language
    allowedLanguages, // global UI language options
    languageRules, // form validation rules
    getUserLanguageFormInput, // utility to normalize form language values
    getLanguageAvatarSrc, // data URL for language flag avatar (PNG or SVG)
    sanitize,
    availableLanguages,
    defaultLanguage,
    updateLanguageConfiguration,
    updatingLanguageConfiguration,
    loadingFromBackendDone,
  };
}
